import { CircularProgress, makeStyles, Typography } from "@material-ui/core"
import clsx from "clsx"
import React, { FC, useState } from "react"
import { NudgeDomain } from "../../utils/constants"
import { colors } from "../../utils/theme"
import { TechniqueProfileData } from "../../utils/types"
import { LiteButton } from "../atoms/button/Button"
import { TechniqueListWithPercentage } from "./TechniqueListWithPercentage"
import { ViewAllTechniquesDialog } from "./ViewAllTechniquesDialog"

const useStyles = makeStyles((theme) => ({
  buttonText: {
    color: "inherit",
  },
  techniqueProfile: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
    height: 24,
  },
  title: {
    fontWeight: "bold",
    color: colors.neutral.denim,
  },
  technique: {
    color: colors.neutral.denim,
  },
  circle: {
    height: 8,
    width: 8,
    borderRadius: "50%",
    marginRight: theme.spacing(1),
  },
  techniques: {
    paddingRight: theme.spacing(3),
    borderRight: `0.5px solid ${colors.neutral.clay}`,
  },
  mostLeastUsed: {
    paddingLeft: theme.spacing(3),
  },
  content: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
  circleLabel: {
    display: "flex",
    alignItems: "center",
  },
  mostLeastTechnique: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  innerMostLeastTechnique: {
    marginBottom: theme.spacing(2),
  },
  text: {
    color: colors.neutral.denim,
  },
  subtitle: {
    fontWeight: "bold",
    color: colors.neutral.denim,
    marginBottom: theme.spacing(2),
  },
  mostUsed: {
    marginBottom: theme.spacing(2),
  },
  noTechniquesContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  noTechniques: {
    textAlign: "center",
    margin: theme.spacing(4),
    padding: theme.spacing(4),
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
}))

const MostLeastTechnique: FC<{
  label: string
  domain: NudgeDomain
  count: number
  addMargin: boolean
}> = ({ label, domain, count, addMargin }) => {
  const classes = useStyles()
  return (
    <div
      className={clsx(
        classes.mostLeastTechnique,
        addMargin ? classes.innerMostLeastTechnique : undefined
      )}
    >
      <div className={classes.circleLabel}>
        <div
          className={classes.circle}
          style={{ backgroundColor: colors.schedule[domain]?.border ?? "" }}
        />
        <Typography variant="body1" className={classes.technique}>
          {label}
        </Typography>
      </div>
      <Typography variant="body1" className={classes.text}>
        {count}
      </Typography>
    </div>
  )
}

const TechniqueProfile: FC<{ techniqueProfileData?: TechniqueProfileData }> = ({
  techniqueProfileData,
}) => {
  const classes = useStyles()

  const [openAllTechniquesDialog, setOpenAllTechniquesDialog] =
    useState<boolean>(false)

  const percentMultiplier =
    1 / (techniqueProfileData?.topTenTechniques[0]?.percent ?? 1)

  return (
    <div className={classes.techniqueProfile}>
      <div className={classes.titleSection}>
        <Typography variant="h5" className={classes.title}>
          Top 10 Used Intervention Techniques
        </Typography>
        <LiteButton onClick={() => setOpenAllTechniquesDialog(true)}>
          <Typography variant="body1" className={classes.buttonText}>
            View All
          </Typography>
        </LiteButton>
      </div>
      {techniqueProfileData &&
        techniqueProfileData.topTenTechniques.filter((x) => x.active).length >
          0 && (
          <div className={classes.content}>
            <div className={classes.techniques}>
              <TechniqueListWithPercentage
                techniques={techniqueProfileData.topTenTechniques}
                percentMultiplier={percentMultiplier}
              />
            </div>
            <div className={classes.mostLeastUsed}>
              <div className={classes.mostUsed}>
                <Typography variant="h6" className={classes.subtitle}>
                  Techniques Used Most Often
                </Typography>
                {techniqueProfileData?.mostUsed
                  .filter((x) => x.active)
                  .map((technique, index, array) => (
                    <MostLeastTechnique
                      key={`${technique.label}${index}-most`}
                      addMargin={index < array.length - 1}
                      {...technique}
                    />
                  ))}
              </div>
              <Typography variant="h6" className={classes.subtitle}>
                Techniques Used Least Often
              </Typography>
              {techniqueProfileData?.leastUsed
                .filter((x) => x.active)
                .map((technique, index, array) => (
                  <MostLeastTechnique
                    key={`${technique.label}${index}-least`}
                    addMargin={index < array.length - 1}
                    {...technique}
                  />
                ))}
            </div>
          </div>
        )}

      {techniqueProfileData &&
        techniqueProfileData.topTenTechniques.length === 0 && (
          <div className={classes.noTechniquesContainer}>
            <Typography variant="body1" className={classes.noTechniques}>
              Once your practitioners start assigning nudges to their clients,
              the most used techniques and domains will be shown here.
            </Typography>
          </div>
        )}

      {!techniqueProfileData && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
      <ViewAllTechniquesDialog
        open={openAllTechniquesDialog}
        onClose={() => setOpenAllTechniquesDialog(false)}
        techniques={techniqueProfileData?.all}
        percentMultiplier={percentMultiplier}
      />
    </div>
  )
}

export default TechniqueProfile
